import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { formatTourId } from '../utils/tour';
import { expirationStatus, expirationStyle, expirationLabel } from '../utils/supplies';

const STATUS_BADGE = {
  draft: <span className="badge badge-draft">Draft</span>,
  submitted: <span className="badge badge-submitted">Pending Review</span>,
  acknowledged: <span className="badge badge-acknowledged">In Progress</span>,
  resolved: <span className="badge badge-resolved">Resolved</span>,
  escalated: <span className="badge badge-escalated">⚠ Escalated</span>,
};

function ScoreChip({ score }) {
  const s = parseFloat(score);
  const cls = s >= 90 ? 'score-high' : s >= 75 ? 'score-mid' : 'score-low';
  return <span className={`score-circle ${cls}`}>{s.toFixed(0)}%</span>;
}

const TOUR_ROLES = ['staff', 'building_admin', 'regional_manager', 'executive_staff', 'super_admin'];

export default function Dashboard() {
  const { user, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiring, setExpiring] = useState([]);

  useEffect(() => {
    API.get('/admin/dashboard').then(r => setData(r.data)).finally(() => setLoading(false));
    // Load top-10 expiring supplies in next 30 days. Backend scopes by role
    // (super_admin / executive_staff see all, regional_manager sees their region,
    // building_admin / staff see their facility). Returns sorted by expiration date.
    API.get('/supplies?expiring_within_days=30').then(r => setExpiring(r.data.slice(0, 10))).catch(() => setExpiring([]));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  if (!data) return null;

  const { stats, recentTours } = data;
  const canStartTour = TOUR_ROLES.includes(user?.role);

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <p>Welcome back, {user.firstName} — {new Date().toLocaleDateString('en-US', {weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p>
        </div>
        {canStartTour && (
          <Link to="/tours/new" className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Start New Tour
          </Link>
        )}
      </div>

      <div className="page-body">
        {parseInt(stats?.escalated) > 0 && (
          <div className="alert-banner alert-danger" style={{marginBottom:20}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 22 20 2 20"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <strong>{stats.escalated} tour{stats.escalated > 1 ? 's' : ''}</strong> have been escalated due to unresolved issues.
            <Link to="/tours?status=escalated" style={{marginLeft:8,fontWeight:700}}>View now →</Link>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Tours (30 days)</div>
            <div className="stat-value">{stats?.total_tours || 0}</div>
            <div className="stat-sub">facility inspections</div>
          </div>
          <div className="stat-card info">
            <div className="stat-label">Pending Review</div>
            <div className="stat-value">{stats?.pending_acknowledgment || 0}</div>
            <div className="stat-sub">awaiting admin response</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">In Progress</div>
            <div className="stat-value">{stats?.in_progress || 0}</div>
            <div className="stat-sub">issues being addressed</div>
          </div>
          <div className={`stat-card ${parseInt(stats?.escalated) > 0 ? 'danger' : ''}`}>
            <div className="stat-label">Escalated</div>
            <div className="stat-value" style={parseInt(stats?.escalated) === 0 ? {color:'var(--gray-500)'} : {}}>{stats?.escalated || 0}</div>
            <div className="stat-sub">past 48-hour window</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Avg Compliance</div>
            <div className="stat-value">{stats?.avg_compliance ? `${parseFloat(stats.avg_compliance).toFixed(1)}%` : '—'}</div>
            <div className="stat-sub">across all facilities</div>
          </div>
        </div>

        {/* Expiring supplies widget — only shown when there are items expiring soon */}
        {expiring.length > 0 && (
          <div className="card" style={{marginBottom:20,borderLeft:'4px solid var(--amber)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <h3 className="card-title" style={{marginBottom:0}}>📦 Supplies Expiring Soon</h3>
              <Link to="/supplies" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:12}}>
              {expiring.length} {expiring.length === 1 ? 'item is' : 'items are'} expiring in the next 30 days{expiring.length === 10 ? ' (showing first 10)' : ''}.
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Facility</th>
                    <th>Quantity</th>
                    <th>Expiration</th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.map(s => {
                    const status = expirationStatus(s.expiration_date);
                    return (
                      <tr key={s.id}>
                        <td style={{fontWeight:600}}>
                          <Link to={`/facilities/${s.facility_id}/supplies`} style={{color:'var(--text-primary)',textDecoration:'none'}}>
                            {s.item_name}
                          </Link>
                          {s.category && <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:400}}>{s.category}</div>}
                        </td>
                        <td style={{fontSize:13}}>{s.facility_name}</td>
                        <td>{s.quantity}{s.unit ? ` ${s.unit}` : ''}</td>
                        <td>
                          <div style={expirationStyle(status)}>{format(new Date(s.expiration_date), 'MMM d, yyyy')}</div>
                          <div style={{fontSize:11,...expirationStyle(status)}}>{expirationLabel(s.expiration_date)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <h3 className="card-title" style={{marginBottom:0}}>Recent Tours</h3>
            <Link to="/tours" className="btn btn-secondary btn-sm">View All</Link>
          </div>
          {recentTours?.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No tours yet</h3>
              <p>Tours will appear here after they are submitted.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tour ID</th>
                    <th>Facility</th>
                    <th>Conducted By</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>Open Issues</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentTours.map(tour => (
                    <tr key={tour.id}>
                      <td style={{fontFamily:'DM Mono, monospace',fontSize:13,color:'var(--gray-700)'}}>
                        {formatTourId(tour.tour_number)}
                      </td>
                      <td style={{fontWeight:600}}>{tour.facility_name}</td>
                      <td>{tour.staff_name}</td>
                      <td style={{color:'var(--gray-500)',fontSize:13}}>
                        {tour.submitted_at ? format(new Date(tour.submitted_at), 'MMM d, yyyy') : '—'}
                      </td>
                      <td>{tour.compliance_score ? <ScoreChip score={tour.compliance_score}/> : '—'}</td>
                      <td>
                        {parseInt(tour.open_issues) > 0 ? (
                          <span style={{color:'var(--red)',fontWeight:700}}>{tour.open_issues} open</span>
                        ) : (
                          <span style={{color:'var(--green)'}}>✓ Clear</span>
                        )}
                      </td>
                      <td>{STATUS_BADGE[tour.status] || tour.status}</td>
                      <td>
                        <Link to={`/tours/${tour.id}`} className="btn btn-secondary btn-sm">Review</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
